/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import {
  abs,
  atan2,
  sin,
} from 'mathjs';
import _forEach from 'lodash/forEach';
import utilityService from '../../services/utility.service';
import { is_iphone } from '../../services/utils/is_iphone';
import { is_ipad } from '../../services/utils/is_ipad';
import httpService from '../../services/http.service';
import d3 from '../../globalexports/d3.export';
import mapLeafService from '../Leafletmapview/Leafletmapview.service';
import bnChartDataService from '../../services/chartData.service';
import AirportService from '../Airport/Airport.service';
import GlobeConst from '../../constants/globe.constant';
import ChartConst from '../../constants/chart.constant';
import bnd3Service from '../../services/bnd3.service';
import bnChartDataProcessService from '../../services/chartDataProcess.service';
import StormTrackConst from '../../constants/stormTrack.const';
import { getAirportMetarData } from '../../services/metar/getAirportMetarData';

const globeviewUtilityService = () => {
  const content = {};
  let planeScale = 1;
  const {
    flightDotRadius,
  } = GlobeConst;
  let {
    flightDotMos,
  } = GlobeConst;
  let flightDotMetar = (GlobeConst.metarSizePercentOfMos * flightDotMos) / 100;
  if (is_iphone()) {
    planeScale = 1.3;
    flightDotMos = (GlobeConst.flightDotiPhoneMosPercent * flightDotMos) / 100;
    flightDotMetar = (GlobeConst.metarSizePercentOfMos * flightDotMos) / 100;
  } else if (is_ipad()) {
    planeScale = 1;
    flightDotMos = (GlobeConst.flightDotiPadMosPercent * flightDotMos) / 100;
    flightDotMetar = (GlobeConst.metarSizePercentOfMos * flightDotMos) / 100;
  }
  content.getFlightDotsSizes = () => ({
    flightDotRadius,
    flightDotMos,
    flightDotMetar,
    planeScale,
  });
  content.getLineString = (settings) => {
    const start = utilityService.getMatchedAirportObject(settings.start);
    const mid1 = utilityService.getMatchedAirportObject(settings.mid1);
    const mid2 = utilityService.getMatchedAirportObject(settings.mid2);
    const end = utilityService.getMatchedAirportObject(settings.end);
    const lineStringPoints = [];
    if (start) {
      lineStringPoints.push([start.lng, start.lat]);
    }
    if (mid1) {
      lineStringPoints.push([mid1.lng, mid1.lat]);
    }
    if (mid2) {
      lineStringPoints.push([mid2.lng, mid2.lat]);
    }
    if (end) {
      lineStringPoints.push([end.lng, end.lat]);
    }
    return lineStringPoints;
  };
  content.checkIfItIsValidAviationMet = (airmet, currentPlaneTime) => {
    if (currentPlaneTime >= airmet.validFromLocal && currentPlaneTime <= airmet.expireOnLocal) {
      return true;
    }
    return false;
  };
  content.getDbzMappingColor = (dbz) => {
    const dbzColorScale = [
      '4B00A0',
      '0033FF',
      '0065FF',
      '0196FF',
      '64AFFF',
      '007C4B',
      '00B001',
      '4BE101',
      'CCFF00',
      'FFFF01',
      'FEAF00',
      'FF7D00',
      'FF4B00',
      'AF0000',
      'C8007D',
      'FF00FF',
    ];
    const color = `#${dbzColorScale[Math.floor(Math.floor(dbz + 15) / 5) - 1]}`;
    return color;
  };

  content.calculateBoundingBox = (projection, width, height) => {
    const topLeftCornerPx = [0, 0];
    const topRightCornerPx = [width, 0];
    const bottomRightCornerPx = [width, height];
    const bottomleftCornerPx = [0, height];

    const topLeftLngLatCoordinates = projection.invert(topLeftCornerPx);
    const bottomRightLngLatCoordinates = projection.invert(bottomRightCornerPx);
    const topRightLngLatCoordinates = projection.invert(topRightCornerPx);
    const bottomLeftLngLatCoordinates = projection.invert(bottomleftCornerPx);
    return {
      topLeftLngLatCoordinates,
      topRightLngLatCoordinates,
      bottomLeftLngLatCoordinates,
      bottomRightLngLatCoordinates,
    };
  };
  content.getZoomPercent = (scaleExtent, currentScale) => {
    const total = scaleExtent[1];
    const zoomPercent = (currentScale * 100) / total;
    return zoomPercent;
  };
  function getPromises() {
    const promises = [
      httpService.http_get('static/json/usStates.json'),
      // httpService.http_get('static/json/world-110m.json'),
      // httpService.http_get('static/json/geoMap.geojson'),
    ];
    return promises;
  }
  content.getGlobeStaticData = async () => {
    const resolves = await Promise.all(getPromises());
    // const world = resolves[0];
    // const geoMapJson = resolves[1];
    const usStatesJson = resolves[0];
    // const land = window.topojson.feature(world, world.objects.countries);
    // const topo = window.topojson.topology([geoMapJson]);
    // const usaStates = window.topojson.mesh(topo);
    return {
      // land,
      usaStates: usStatesJson,
    };
  };
  content.getFilterObj = () => {
    const metLayersSelector = bnChartDataProcessService.getMetsForLayer();
    const suppressionMatrix = bnd3Service.getIfGairmetAndSigmentChecked(metLayersSelector);
    suppressionMatrix.mets = metLayersSelector;
    return suppressionMatrix;
  };
  content.shouldShowMetarAndMos = () => {
    const filter = content.getSuppressedMet();
    return (filter.indexOf(ChartConst.flightCategoryKey) === -1);
  };
  content.shouldShowRadar = () => {
    const filter = content.getSuppressedMet();
    return (filter.indexOf(ChartConst.RadarFilterKey) === -1);
  };

  content.shouldShowStormtrack = () => {
    const filter = content.getSuppressedMet();
    return (
      filter.indexOf(ChartConst.RadarFilterKey) === -1
      && filter.indexOf(ChartConst.stormTrackFilterKey) === -1
    );
  };

  content.getSuppressedMet = () => utilityService.getSuppressedMetFromLocalStorage();

  content.isGairmetLayerOn = (filter) => (filter.indexOf(ChartConst.gairmetsLayerKey) === -1);

  content.isSigmetLayerOn = (filter) => (filter.indexOf(ChartConst.sigmetsLayerKey) === -1);

  content.shouldShowAirmet = (airmet) => {
    const filter = content.getSuppressedMet();
    if (airmet.isSigmet) {
      if (content.isSigmetLayerOn(filter)) {
        return true;
      }
      return false;
    }
    if (content.isGairmetLayerOn(filter)) {
      if (filter.indexOf(airmet.hazard_type_identifier) > -1) {
        return false;
      }
      return true;
    }
    return false;
  };
  content.closest_angle = (touches, a) => {
    const iterationMock = [0, 1];
    const centroid = iterationMock.map((i) => d3.mean(touches, (d) => d[i]));
    const angles = touches.map((d) => -atan2(d[1] - centroid[1], d[0] - centroid[0]));
    const i = d3.scan(angles.map((b) => abs(sin((b - a) / 2))));
    return angles[i];
  };

  content.fetchTravelRouteAirportTooltipContentHtml = (
    airportForecast,
    settings,
  ) => mapLeafService.getPopoverHtml(airportForecast, settings, false, true);

  content.fetchMetarTooltipContentHtml = async (
    airportForecast,
    settings,
  ) => {
    const metarAirportData = await getAirportMetarData(airportForecast);
    const data = mapLeafService.getPopoverHtml(metarAirportData[0], settings, false, true);
    return data;
  };

  content.fetchMosAirportTooltipContentHtml = async (airport, settings) => {
    const settingForMos = {
      start: airport.icao_id,
      isForMos: true,
    };
    const airportMosInfo = await AirportService.getMeteogramData(settingForMos, 'meteogram');
    if (airportMosInfo && airport.flightMode && airportMosInfo[airport.flightMode]) {
      const validForecast = bnChartDataService.findClosestRecord(
        airportMosInfo[airport.flightMode],
        airport.forecast_ts_local,
      );
      // var validForecast = airportMosInfo[airport.flightMode].forecast[airport.closestIndex];
      // if (airport.isMavFrl && airport.closestDateObjFromMav) {
      //     validForecast = airportMosInfo.mav.forecast[airport.closestDateObjFromMav.index];
      // }
      // validForecast.frl = airport.frl;
      // This will make sure that even if mav record was used
      // to fill LAMP data then also tooltip shows right text
      validForecast.forecast_ts_local = airport.forecast_ts_local;
      return mapLeafService.getPopoverHtml(
        validForecast,
        settings,
        false,
        true,
      );
    }
  };

  content.findMatchedAirportFromListAtGivenLatLng = (
    clickLngLatPoint,
    airportList,
    radiusOfDot,
    projection,
  ) => {
    const firstLevel = airportList.filter((airport) => {
      let pxPositionOfAirport = projection([airport.lng, airport.lat]);
      pxPositionOfAirport = {
        x: pxPositionOfAirport[0],
        y: pxPositionOfAirport[1],
      };
      if (((pxPositionOfAirport.x - clickLngLatPoint.x) ** 2)
        + ((pxPositionOfAirport.y - clickLngLatPoint.y) ** 2) <= (radiusOfDot ** 2)) {
        return true;
      }
      return false;
    });
    return firstLevel[0];
  };
  content.findMatchedStormAtGivenLatLng = (
    clickLngLatPoint,
    radarSiteToBeDrawn,
    radiusOfDot,
    projection,
  ) => {
    let matchedStorm = null;
    radarSiteToBeDrawn.storms.forEach((storm) => {
      if (!matchedStorm) {
        let pxPositionOfAirport = projection([storm.fcst[0][1], storm.fcst[0][0]]);
        pxPositionOfAirport = {
          x: pxPositionOfAirport[0],
          y: pxPositionOfAirport[1],
        };
        if (((pxPositionOfAirport.x - clickLngLatPoint.x) ** 2)
      + ((pxPositionOfAirport.y - clickLngLatPoint.y) ** 2) <= (radiusOfDot ** 2)) {
          matchedStorm = storm;
          matchedStorm.isFilterMode = radarSiteToBeDrawn.isFilterMode;
        }
      }
    });
    return matchedStorm;
  };
  content.getBaseTooltipHtml = () => `<div id="globviewtooltip">
                <a class="popup-close-button" id="popup-close-button">×</a>
                <div id="content">
                </div>
                <div class="mapview-bubble-container">
                  <div class="mapview-bubble"></div>
                </div>
              </div>`;
  content.getMetPopoverHtml = (polygonList, dateFormat) => {
    if (!polygonList.length) {
      return '';
    }
    let html = "<div id='conditiontooltip' class='conditions card met-condition'>";
    _forEach(polygonList, (value) => {
      html += "<div class='airport'>";
      html += `${value.tooltip.prefix}: `;
      html += '<b>';
      html += value.real_altitude_tooltip_text;
      if (value.validTimeFromLocalReal && value.validTimeToLocalReal) {
        html
          += ` ${dateFormat(value.validTimeFromLocalReal)}-${dateFormat(value.validTimeToLocalReal)}`;
      } else if (value.validTimeFromLocalReal) {
        html += ` ${dateFormat(value.validTimeFromLocalReal)}`;
      }
      html += '</b>';
      html += '</div>';
    });
    html += '</div>';
    return html;
  };
  content.getStormPopoverHtml = (storm, dateFormat) => {
    if (!storm || !storm.si) {
      return '';
    }
    let html = "<div id='conditiontooltip' class='conditions card storm-track-tooltip'>";
    html += "<div class='airport'>";
    html += `<h3 class='airport storm-id'>${storm.radarId} - Cell ${storm.id} ${dateFormat(storm.utcTimeStamp)}</h3>`;
    html += '<div class="storm-info">';
    Object.keys(storm.si).forEach((siKey) => {
      const value = storm.si[siKey];
      const measurement = StormTrackConst[siKey].label;
      const { unit } = StormTrackConst[siKey];
      html += `<div><span style="text-transform: capitalize;">${measurement}:</span> <b>${value} ${unit}</b></div>`;
    });
    html += '</div>';
    html += '</div>';
    html += '</div>';
    return html;
  };
  return content;
};
export default globeviewUtilityService();
